import FiiDii from '../../assets/icons/fiiDii'
import Chart from '../../assets/icons/Chart'
import { generatePath, Link as RouterLink, matchPath, useLocation, useNavigate } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import { Box, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper } from '@mui/material'
import React, { useCallback, useState } from 'react'
import Screener from '../../assets/icons/Screener'
import { SECURITY_URL_PARAM, ViewType } from '../../assets/constants'
import { UserProfileMenu } from './UserProfileMenu'
import Tick2TradeSymbol from '../../assets/icons/Tick2TradeSymbol'
import { useSelector } from 'react-redux'
import { selectNavigationUrls } from '../../selectors/common'
import OptionChain from '../../assets/icons/OptionChain'
import IvPlots from '../../assets/icons/IvPlots'
import StrategyBuilder from '../../assets/icons/StrategyBuilder'
import Tick2TradeLogo from '../../assets/icons/Tick2TradeLogo'
import SimulatorIcon from '../../assets/icons/SimulatorIcon'
import ReportsIcon from '../../assets/icons/ReportsIcon'
import { resetStrategyBuilder, setSelectedTab } from '../../reducer/strategyBuilder.reducer'
import { useDispatch } from 'react-redux'
import { ActionType, useUndoRedoContext } from '../UndoRedoFunctonalities/UndoRedo.context'
import { setSimulatorOrderDataFunction } from '../../reducer/simulator.reducer'
import { selectSimulatorDate } from '../../selectors/simulator'
import { StrategyBuilderTab } from '../strategyBuilder/header'
import ScalperIcon from '../../assets/icons/ScalperIcon'
import HeatmapIcon from '../../assets/icons/HeatmapIcon'

const buttons = [
    {
        element: <Chart fontSize="large" />,
        label: 'Chart',
        route: [ROUTES.chart, ROUTES.symbol, ROUTES.spread]
    },
    {
        element: <OptionChain fontSize="large" />,
        label: 'Option Chain',
        route: [ROUTES.optionChain]
    },
    {
        element: <StrategyBuilder fontSize="large" />,
        label: 'Strategy Builder',
        route: [ROUTES.strategyBuilder]
    },
    {
        element: <IvPlots fontSize="large" />,
        label: 'OI Analysis',
        route: [ROUTES.oiOverview, ROUTES.openInterestChange, ROUTES.openInterest, ROUTES.multiStrikeChart]
    },
    {
        element: <Screener fontSize="large" />,
        label: 'Screener',
        route: [ROUTES.screener]
    },
    {
        element: <HeatmapIcon fontSize="large" />,
        label: 'Heatmap',
        route: [ROUTES.heatmap]
    },
    {
        element: <ScalperIcon fontSize='large' />,
        label: 'Scalper',
        route: [ROUTES.scalper]
    },
    {
        element: <SimulatorIcon fontSize="large" />,
        label: 'Simulator',
        route: [ROUTES.simulator]
    },
    {
        element: <FiiDii fontSize="large" />,
        label: 'FII/DII',
        route: [ROUTES.fiiDii, ROUTES.fiiDiiSummary, ROUTES.fiiDiiFuturesOptions, ROUTES.fiiDiiCashMarket, ROUTES.fiiDiiFiiHistory]
    },
    {
        element: <ReportsIcon fontSize="large" />,
        label: 'Reports',
        route: [ROUTES.reports]
    }
    // {
    //    label:'Automation',
    //     route:[ROUTES.automation]
    // },
]
const SideBarLeft = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const navigationUrls = useSelector(selectNavigationUrls)
    const { manager } = useUndoRedoContext()
    const { push } = useUndoRedoContext()
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const [hovered, setHovered] = useState(false)
    const onLogoClick = useCallback(() => {
        const queryParams = new URLSearchParams(location.search)
        const mainSecurity = queryParams.get(SECURITY_URL_PARAM)
        let finalRoute = ROUTES.base
        if (mainSecurity) {
            finalRoute += `?${SECURITY_URL_PARAM}=${mainSecurity}`
        }
        navigate(finalRoute)
    }, [location.search, navigate])

    const getNavRoute = useCallback((route: string) => {
        if (route === ROUTES.optionChain) {
            return (
                navigationUrls?.[ViewType.OPTION_CHAIN] ||
                generatePath(ROUTES.optionChain, {
                    exchange: 'NSE',
                    symbol: 'NIFTY_50'
                })
            )
        } else if (route === ROUTES.openInterest) {
            return (
                navigationUrls?.[ViewType.OPEN_INTEREST] ||
                generatePath(ROUTES.openInterest, {
                    exchange: 'NSE',
                    symbol: 'NIFTY_50'
                })
            )
        } else if (route === ROUTES.simulator) {
            return (
                navigationUrls?.[ViewType.SIMULATOR] ||
                generatePath(ROUTES.simulator, {
                    exchange: 'NSE',
                    symbol: 'NIFTY_50'
                })
            )
        } else if (route === ROUTES.strategyBuilder) {
            return (
                navigationUrls?.[ViewType.STRATEGY_BUILDER] ||
                generatePath(ROUTES.strategyBuilder, {
                    exchange: 'NSE',
                    symbol: 'NIFTY_50'
                })
            )
        } else {
            return generatePath(route, { layoutId: '' })
        }
    }, [navigationUrls])

    return (
        <Drawer
            id="left-navigation"
            variant="permanent"
            open={hovered}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            component={Paper}
            PaperProps={{
                sx: {
                    width: hovered ? '230px' : '56px',
                    position: 'relative',
                    border: 'none',
                    borderRight: (theme) =>
                        hovered && theme.palette.mode === 'dark'
                            ? '1px solid #2b2e38'
                            : 'none',
                    boxShadow: (theme) => (hovered ? theme.shadows[2] : 'none'),
                    height: '100%',
                    borderRadius: '4px 4px 4px 0',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <Box
                sx={{
                    flexGrow: 1
                }}
            >
                <IconButton
                    sx={{
                        width: 'max-content',
                        marginLeft: 2,
                        marginY: 1,
                        padding: 0,
                        justifyContent: 'start',
                        '&:hover, &:focus, &:active': {
                            color: 'inherit !important'
                        }
                    }}
                    color={'inherit'}
                    aria-label="tick2trade home"
                    onClick={onLogoClick}
                    data-event={`Clicked on tick2trade logo`}
                >
                    {hovered ? <Tick2TradeSymbol /> : <Tick2TradeLogo fontSize={'large'} />}
                </IconButton>
                <List id={'side-bar-left-buttons'} sx={{ padding: 0 }}>
                    {buttons.map((btn, i) => {
                        // if (!btn.route || btn.route.length === 0) return null;
                        const route = btn.route[0]
                        const matchedRoute = btn.route.find((rt) => !!matchPath(rt, window.location.pathname))
                        const active = !!matchedRoute
                        const isScreenerButton = route == ROUTES.screener
                        const navRoute = (isScreenerButton) ? ROUTES.screenerExplore : active ? window.location.pathname : getNavRoute(route)
                        return (
                            <ListItem key={i} disablePadding>
                            <ListItemButton
                                data-event-source={'left-side-bar'}
                                data-event={`${btn.label} Icon Clicked`}
                                aria-label={btn.label}
                                aria-current={active ? 'page' : undefined}
                                sx={{
                                    padding: '4px 10px',
                                    backgroundColor: (theme) => (active ? theme.palette.background.default : null),
                                    '&:hover': {
                                        backgroundColor: (theme) => (active ? theme.palette.background.default : null)
                                    }
                                }}
                                onClick={(e) => {
                                    if (btn.route[0]==ROUTES.strategyBuilder) {
                                        dispatch(resetStrategyBuilder())
                                    } else if(btn.route[0]==ROUTES.simulator){
                                        manager.clear({})
                                        push({ previousTimeStamp: simulatorTimestamp, timeStamp: simulatorTimestamp }, 'simulator time change', ActionType.SIMULATOR_TIME_CHANGE)
                                        setSimulatorOrderDataFunction({ orders: [], resetOrderState: true })
                                        dispatch(setSelectedTab(StrategyBuilderTab.Payoff))
                                    }
                                }}
                                component={RouterLink}
                                to={navRoute}
                            >
                                <ListItem
                                    component="div"
                                    sx={{
                                        borderRadius: '4px',
                                        width: hovered ? '190px' : 'max-content',
                                        justifyContent: 'start',
                                        padding: '0'
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: '30px',
                                            strokeWidth: 1.5
                                        }}
                                    >
                                        {btn.element}
                                    </ListItemIcon>
                                    {hovered && (
                                        <ListItemText
                                            primary={btn.label}
                                            sx={{
                                                marginLeft: '6px',
                                                fontSize: '0.9rem !important',
                                                fontWeight: 500
                                            }}
                                        />
                                    )}
                                </ListItem>
                            </ListItemButton>
                            </ListItem>
                        )
                    })}
                </List>
            </Box>
            <List component="div" sx={{ p: 0, py: 1 }}>
                <UserProfileMenu hovered={hovered} onCloseSidebar={() => setHovered(false)} />
            </List>
        </Drawer>
    )
}

export default React.memo(SideBarLeft)
